import classNames from 'classnames';
import styles from './Header.module.scss';
import { UI_SkipToMain } from 'ui/components/1-atoms/SkipToMain/SkipToMain';
import { UI_Container } from 'ui/components/4-habitats/Container/Container';
import { NavigationProps, UI_Navigation } from 'ui/components/3-organisms/Navigation/Navigation';
import { ServiceNavigationProps, UI_ServiceNavigation } from 'ui/components/3-organisms/ServiceNavigation/ServiceNavigation';
import { useEffect, useRef, useState } from 'react';
import BurgerIcon from 'ui/icons/icon-burger.svg';
import BurgerCloseIcon from 'ui/icons/icon-burger-close.svg';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import type { BodyScrollOptions } from 'body-scroll-lock';
import { UI_Icon } from 'ui/components/1-atoms/Media/Icon/Icon';
import SearchIcon from 'ui/icons/icon_search.svg';
import { UI_LinkWithIcon } from 'ui/components/1-atoms/Action/LinkWithIcon/LinkWithIcon';

export interface HeaderProps extends Omit<NavigationProps, 'ref'>, ServiceNavigationProps {
	className?: string;
	heading: string;
	logoArea?: React.ReactNode;
	languageSelector?: React.ReactNode;
	search?: {
		url: string;
		text: string;
	};
	reset?: number;
	skipToMain: {
		text: string;
		explainerText: string;
	};
	toggleMenuLabel: string;
	serviceNavigationHeading?: string;
}

export const UI_Header = ({
	className,
	navigationItems,
	maxLevels,
	logoArea,
	languageSelector,
	serviceNavigationItems,
	search,
	reset,
	skipToMain,
	toggleMenuLabel,
	serviceNavigationHeading = 'Sub menu', //Todo: Should come from dictionary item
}: HeaderProps) => {
	const [isNavOpen, setIsNavOpen] = useState(false);
	const navRef = useRef<HTMLElement>(null);

	useEffect(() => {
		setIsNavOpen(false);
	}, [reset]);

	useEffect(() => {
		const navElement = navRef.current;
		const options: BodyScrollOptions = {
			reserveScrollBarGap: true,
		};
		if (isNavOpen) disableBodyScroll(navElement, options);
		if (!isNavOpen) enableBodyScroll(navElement, options);

		return () => {
			enableBodyScroll(navElement, options);
		};
	}, [isNavOpen]);

	return (
		<header className={classNames(styles.Header, className, { [styles.Header___navigationIsOpen]: isNavOpen })} role="banner">
			<UI_SkipToMain link="#main" title={skipToMain?.explainerText}>
				{skipToMain?.text}
			</UI_SkipToMain>
			<UI_Container className={styles.Header_container} width="Standard">
				<div className={styles.Header_grid}>
					<div className={styles.Header_logo}>{logoArea}</div>
					<div className={styles.Header_language}>{languageSelector}</div>

					<div className={styles.Header_search}>
						{search?.url && (
							<UI_LinkWithIcon url={search.url} icon={<SearchIcon />} text={search.text} hideTextOnMobile iconSize="lg" gap="small" />
						)}
					</div>

					<UI_ServiceNavigation
						className={styles.Header_serviceNavigation}
						heading={serviceNavigationHeading}
						serviceNavigationItems={serviceNavigationItems}
					/>
					{navigationItems && (
						<button
							className={styles.Header_navigationToggle}
							aria-label={toggleMenuLabel}
							aria-expanded={isNavOpen}
							aria-controls="primary-navigation"
							onClick={() => setIsNavOpen(!isNavOpen)}
						>
							{!isNavOpen && (
								<UI_Icon size="lg">
									<BurgerIcon />
								</UI_Icon>
							)}
							{isNavOpen && (
								<UI_Icon size="lg">
									<BurgerCloseIcon />
								</UI_Icon>
							)}
						</button>
					)}
				</div>
				<div className={styles.Header_divider}></div>
			</UI_Container>
			<UI_Navigation className={styles.Header_navigation} navigationItems={navigationItems} maxLevels={maxLevels} ref={navRef}>
				<div className={classNames(styles.Header_language, styles.Header_language___mobile)}>{languageSelector}</div>
				<UI_ServiceNavigation
					heading={serviceNavigationHeading}
					className={classNames(styles.Header_serviceNavigation, styles.Header_serviceNavigation___mobile)}
					mobile
					serviceNavigationItems={serviceNavigationItems}
				/>
			</UI_Navigation>
		</header>
	);
};
